import type { Feature } from 'geojson';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Overlay, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import ActivityCards from 'components/ActivityCards';
import Drawer from 'components/Drawer';
import FloatingResourceAddMenu from 'components/FloatingResourceAddMenu';
import Grid from 'components/Grid';
import Map from 'components/Map';
import { GEOJSON_PROPERTY } from 'components/Map/hooks/useFeatures/constants';
import Page from 'components/Page';
import WarrantyCards from 'components/WarrantyCards';
import AddButton from 'components/buttons/AddButton';

import useFeatureFlags from 'hooks/useFeatureFlags';
import usePageRoutes from 'hooks/usePageRoutes';

import useData from './hooks/useData';

import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const HomePage: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const navigate = useNavigate();
    const { data, fetch, focusGeometry, geometry } = useData();
    const { applicationDetailsPage } = usePageRoutes();
    const { pathname } = useLocation();

    const { WARRANTY_CARDS_FEATURE_FLAG } = useFeatureFlags();

    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const handleOpenAddMenu = () => setIsAddMenuOpen(true);
    const handleCloseAddMenu = () => setIsAddMenuOpen(false);

    useEffect(() => {
        if (pathname !== '/') {
            return;
        }

        (async () => {
            await fetch();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    const handleFeatureSelect = (feature: Feature) => {
        const applicationId = feature.properties?.[GEOJSON_PROPERTY.EXT_APPLICATION_ID];

        if (!applicationId) {
            return;
        }

        navigate(applicationDetailsPage(applicationId));
    };

    return (
        <Page className={className} testId={testId}>
            <Map
                focusGeometry={focusGeometry}
                geometry={geometry}
                mapInteractionOptions={{ hasAddressSearch: true }}
                onFeatureSelect={handleFeatureSelect}
            />

            <VerticalContainer className={styles.verticalContainer}>
                <Wrapper className={styles.resourceAddButton}>
                    <AddButton onClick={handleOpenAddMenu} testId={`${testId}ResourceAddButton`} />
                </Wrapper>

                <Drawer className={styles.drawer}>
                    <Grid className={styles.grid}>
                        <ActivityCards
                            className={styles.activityCards}
                            items={data?.slice().reverse()}
                        />

                        {WARRANTY_CARDS_FEATURE_FLAG && (
                            <WarrantyCards className={styles.warrantyCards} />
                        )}
                    </Grid>
                </Drawer>
            </VerticalContainer>

            {isAddMenuOpen && (
                <Overlay className={styles.addMenuOverlay}>
                    <FloatingResourceAddMenu onClose={handleCloseAddMenu} />
                </Overlay>
            )}
        </Page>
    );
};

export default HomePage;
